import {
  RatelistType as RatelistTypeEnum,
  RoundingDirection as RoundingDirectionEnum,
  RoundingUnitKind as RoundingUnitKindEnum,
  UnitType as UnitTypeEnum,
} from "@sinch/core/entities/serverEnums";
import type { RoundingDirection, RoundingUnitKind } from "@sinch/core/entities/serverEnums.types";
import { useFormat } from "@sinch/core/hooks/format";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useUnitType } from "@sinch/components/application/Rate/useUnitType";
import type { RateRoundingItemFragment } from "@sinch/core/entities/fragments.types";

type RoundingSummary = Pick<RateRoundingItemFragment, "unitKind" | "base" | "direction" | "grace">;

const DIRECTION_SYMBOLS: Record<RoundingDirection, string> = {
  [RoundingDirectionEnum.Up]: "↑",
  [RoundingDirectionEnum.Down]: "↓",
  [RoundingDirectionEnum.Nearest]: "↕",
  [RoundingDirectionEnum.GraceUp]: "↑",
};

/**
 * A grace period only means something for the "up with grace period" direction
 */
const hasGrace = (rounding: RoundingSummary): rounding is RoundingSummary & { grace: number } =>
  rounding.direction === RoundingDirectionEnum.GraceUp && rounding.grace != null;

/**
 * Human readable labels of rounding definitions: direction, kind and one line summaries like "1h 15m, up"
 */
export const useRoundingLabels = () => {
  const { t } = useTranslation();
  const { duration } = useFormat();
  // the transport distance unit tells whether this agency measures in km or mi
  const { getUnitSymbol, getUnitFormatter } = useUnitType(RatelistTypeEnum.TransportPrice);
  const distanceSymbol = getUnitSymbol(UnitTypeEnum.TransportDistance) ?? "km";
  const formatDistance = getUnitFormatter(UnitTypeEnum.TransportDistance);

  const getDirectionLabel = useCallback(
    (direction: RoundingDirection): string =>
      ({
        [RoundingDirectionEnum.Up]: t("RateRounding.directionUp"),
        [RoundingDirectionEnum.Down]: t("RateRounding.directionDown"),
        [RoundingDirectionEnum.Nearest]: t("RateRounding.directionNearest"),
        [RoundingDirectionEnum.GraceUp]: t("RateRounding.directionGraceUp"),
      })[direction],
    [t]
  );

  const getKindLabel = useCallback(
    (kind: RoundingUnitKind): string =>
      kind === RoundingUnitKindEnum.Time ? t("RateRounding.kindTime") : t("RateRounding.kindDistance"),
    [t]
  );

  /**
   * Amount in the unit of the kind: minutes as compound hours and minutes ("1h 15m", "30m"), distance with its symbol ("5 km")
   */
  const formatAmount = useCallback(
    (kind: RoundingUnitKind, value: number): string =>
      kind === RoundingUnitKindEnum.Time
        ? duration.narrowShort(value * 60)
        : (formatDistance?.(value) ?? `${value} ${distanceSymbol}`),
    [distanceSymbol, duration, formatDistance]
  );

  const formatRounding = useCallback(
    (rounding: RoundingSummary): string => {
      if (rounding.base === 0) {
        return t("RateRounding.noRounding");
      }
      const grace = hasGrace(rounding)
        ? `, ${t("RateRounding.graceShort", { value: formatAmount(rounding.unitKind, rounding.grace) })}`
        : "";
      return `${formatAmount(rounding.unitKind, rounding.base)}, ${getDirectionLabel(rounding.direction)}${grace}`;
    },
    [formatAmount, getDirectionLabel, t]
  );

  /**
   * Chip-sized summary: "≈ 1h 15m ↑", "≈ 30m ↕" (nearest), "≈ 1h ↑ (+ 15m)" (up, the first 15 minutes over the step are free), "∅" (no rounding)
   * - symbols only, so the chip needs no translation; the full wording lives in the tooltip
   */
  const formatRoundingCompact = useCallback(
    (rounding: RoundingSummary): string => {
      if (rounding.base === 0) {
        return "∅";
      }
      const grace = hasGrace(rounding) ? ` (+ ${formatAmount(rounding.unitKind, rounding.grace)})` : "";
      return `≈ ${formatAmount(rounding.unitKind, rounding.base)} ${DIRECTION_SYMBOLS[rounding.direction]}${grace}`;
    },
    [formatAmount]
  );

  return { getDirectionLabel, getKindLabel, formatRounding, formatRoundingCompact };
};
